import useMFAInfo from '$actions/useMFAInfo'

import { useModal } from '$contexts/Modal'

import { useGetPasskeys } from './useGetPasskeys'
import useRemove, { UseRemoveOptions } from './useRemove'
import useSetup, { UseSetupOptions } from './useSetup'
import useUpdate, { UseUpdateOptions } from './useUpdate'

export type UseConfigurePasskeyOptions = {
  mfaInfo: ReturnType<typeof useMFAInfo>
  setupOptions: UseSetupOptions
  updateOptions: UseUpdateOptions
  removeOptions: UseRemoveOptions
  disabled?: boolean
}

export type UseConfigurePasskeyReturn = ReturnType<typeof useConfigurePasskey>

export const useConfigurePasskey = ({
  mfaInfo,
  setupOptions,
  updateOptions,
  removeOptions,
  disabled: forcedDisabled = false
}: UseConfigurePasskeyOptions) => {
  const hasPasskey = mfaInfo.registered_factors.find(
    ({ type }) => type === 'passkey'
  )

  const passkeys = useGetPasskeys()

  const setup = useSetup({
    ...setupOptions,
    onRegistered: () => {
      setupOptions.onRegistered?.()
      mfaInfo.refresh()
      passkeys.refresh()
    }
  })

  const update = useUpdate({
    ...updateOptions,
    onUpdated: () => {
      updateOptions.onUpdated?.()
      mfaInfo.refresh()
      passkeys.refresh()
    }
  })

  const remove = useRemove({
    ...removeOptions,
    onRemoved: () => {
      removeOptions.onRemoved?.()
      mfaInfo.refresh()
      passkeys.refresh()
    }
  })

  const mainModal = useModal({
    title: 'Passkeys',
    onClose: () => {
      update.modal.modalControls.close()
      remove.modal.modalControls.close()
    }
  })

  const disabled =
    forcedDisabled ||
    mfaInfo.isLoading ||
    setup.isAnyLoading ||
    update.isLoading ||
    remove.isLoading ||
    passkeys.loading

  return {
    hasPasskey,
    passkeys,
    mainModal,
    setup,
    update,
    remove,
    disabled
  }
}

export default useConfigurePasskey
